import {
  AccountField,
  BusinessSignUpField,
  CustomerSignUpField,
  EMPTY_STRING,
  OccupationOption,
  ProfileField,
  PromoCodeField,
  ReferralOption,
  TEXT_REQUIRED_MESSAGE,
  verificationCodeRegExp,
} from '../../../utils/forms';

import * as yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { parseMobileNumber } from 'foundshared/src/utils';

import type {
  SignUpFormData} from '../../../utils/forms';


const BUSINESS = 'business';

export const initialFormValues = (prefilledFormValues: Partial<SignUpFormData>): SignUpFormData => ({
  [AccountField.IS_TRADE_CUSTOMER]: false,
  [AccountField.BUYING_BEHALF]: BUSINESS,
  [AccountField.MOBILE]: EMPTY_STRING,
  [AccountField.REFERRAL]: EMPTY_STRING,
  [AccountField.REFERRAL_OTHER]: EMPTY_STRING,
  [AccountField.TERMS]: false,
  [AccountField.VERIFICATION_CODE]: EMPTY_STRING,
  [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: EMPTY_STRING,
  [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: EMPTY_STRING,
  [CustomerSignUpField.COMPANY_NAME]: EMPTY_STRING,
  [CustomerSignUpField.ABN]: EMPTY_STRING,
  [CustomerSignUpField.ABN_STATE]: EMPTY_STRING,
  [CustomerSignUpField.OCCUPATION]: EMPTY_STRING,
  [CustomerSignUpField.OCCUPATION_OTHER]: EMPTY_STRING,
  [CustomerSignUpField.PREFERRED_SUPPLIER_ID]: 0,
  [ProfileField.EMAIL]: EMPTY_STRING,
  [ProfileField.FIRST_NAME]: EMPTY_STRING,
  [ProfileField.LAST_NAME]: EMPTY_STRING,
  [PromoCodeField.PROMO_CODE]: undefined,
  ...prefilledFormValues,
});

export const signUpPayload = (data: SignUpFormData) => {
  if (data[AccountField.BUYING_BEHALF] !== BUSINESS) {
    return {
      // profile
      [ProfileField.FIRST_NAME]: data[ProfileField.FIRST_NAME],
      [ProfileField.LAST_NAME]: data[ProfileField.LAST_NAME],
      [ProfileField.EMAIL]: data[ProfileField.EMAIL],
      // account
      [AccountField.BUYING_BEHALF]: data[AccountField.BUYING_BEHALF],
      [AccountField.MOBILE]: parseMobileNumber(data[AccountField.MOBILE]),
      key: data[AccountField.VERIFICATION_CODE],
      [AccountField.TERMS]: true,
      // customer
      [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: data[CustomerSignUpField.BILLING_ADDRESS_LINE_1],
      [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: data[CustomerSignUpField.BILLING_ADDRESS_LINE_2],

      [CustomerSignUpField.PREFERRED_SUPPLIER_ID]:
        data[CustomerSignUpField.PREFERRED_SUPPLIER_ID] === 0 ? null : data[CustomerSignUpField.PREFERRED_SUPPLIER_ID],
      [CustomerSignUpField.REFERRAL_SLUG]: data[CustomerSignUpField.REFERRAL_SLUG] ?? EMPTY_STRING,
      // promo code
      [PromoCodeField.PROMO_CODE]: data[PromoCodeField.PROMO_CODE],
    };
  }
  return {
    // profile
    [ProfileField.FIRST_NAME]: data[ProfileField.FIRST_NAME],
    [ProfileField.LAST_NAME]: data[ProfileField.LAST_NAME],
    [ProfileField.EMAIL]: data[ProfileField.EMAIL],
    // account
    [AccountField.BUYING_BEHALF]: data[AccountField.BUYING_BEHALF],
    [AccountField.MOBILE]: parseMobileNumber(data[AccountField.MOBILE]),
    key: data[AccountField.VERIFICATION_CODE],
    [AccountField.TERMS]: true,
    // customer
    [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: data[CustomerSignUpField.BILLING_ADDRESS_LINE_1],
    [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: data[CustomerSignUpField.BILLING_ADDRESS_LINE_2],

    [CustomerSignUpField.PREFERRED_SUPPLIER_ID]:
      data[CustomerSignUpField.PREFERRED_SUPPLIER_ID] === 0 ? null : data[CustomerSignUpField.PREFERRED_SUPPLIER_ID],
    [CustomerSignUpField.REFERRAL_SLUG]: data[CustomerSignUpField.REFERRAL_SLUG] ?? EMPTY_STRING,
    // promo code
    [PromoCodeField.PROMO_CODE]: data[PromoCodeField.PROMO_CODE],
    [CustomerSignUpField.BUSINESS_ATTRIBUTES]: {
      [BusinessSignUpField.BUSINESS_NAME]: data[CustomerSignUpField.COMPANY_NAME].split(',')[0],
      [CustomerSignUpField.ABN]: data[CustomerSignUpField.ABN],
      [CustomerSignUpField.ABN_STATE]: data[CustomerSignUpField.ABN_STATE],
      [CustomerSignUpField.OCCUPATION]:
        data[CustomerSignUpField.OCCUPATION] === OccupationOption.OTHER
          ? data[CustomerSignUpField.OCCUPATION_OTHER]
          : data[CustomerSignUpField.OCCUPATION],
    },
  };
};

// TODO: Merge this with TradeCustomerSignUpPayload
export type SignUpPayload = ReturnType<typeof signUpPayload>;

export const mobileSchema = yup
  .string()
  .required(TEXT_REQUIRED_MESSAGE)
  .test('au-number', 'Please provide a valid Australian phone number', (value) =>
    isValidPhoneNumber(value ?? '', 'AS')
  );

export const tradeCustomerSchema = yup.boolean().defined('Please select one of the options.');
export const orderForSchema = yup.string().required('Please select one of the options.');

export const verificationSchema = yup
  .string()
  .matches(verificationCodeRegExp, 'Verification code is not valid')
  .required('Please enter the verification code');

export const companyNameSchema = yup
  .string()
  .when(AccountField.IS_TRADE_CUSTOMER, {
    is: true,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  })
  .when(AccountField.BUYING_BEHALF, {
    is: BUSINESS,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  });

export const occupationSchema = yup
  .string()
  .when(AccountField.IS_TRADE_CUSTOMER, {
    is: true,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  })
  .when(AccountField.BUYING_BEHALF, {
    is: BUSINESS,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  });

export const occupationOtherSchema = yup.string().when(CustomerSignUpField.OCCUPATION, {
  is: OccupationOption.OTHER,
  then: yup.string().required(TEXT_REQUIRED_MESSAGE),
});
export const requiredSchema = yup.string().required(TEXT_REQUIRED_MESSAGE);

export const signUpSchema = yup.object().shape({
  [AccountField.MOBILE]: mobileSchema,
  [AccountField.IS_TRADE_CUSTOMER]: tradeCustomerSchema,
  // [AccountField.TERMS]: yup
  //   .boolean()
  //   .required('You must agree to the terms to place an order')
  //   .equals([true], 'You must agree to the terms to place an order'),
  [AccountField.VERIFICATION_CODE]: verificationSchema,
  [AccountField.REFERRAL]: yup.string(),
  [AccountField.REFERRAL_OTHER]: yup.string().when(AccountField.REFERRAL, {
    is: ReferralOption.OTHER,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  }),
  [CustomerSignUpField.COMPANY_NAME]: companyNameSchema,
  // [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: yup.string().required(TEXT_REQUIRED_MESSAGE),
  // [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: yup.string(),
  [CustomerSignUpField.OCCUPATION]: occupationSchema,
  [CustomerSignUpField.OCCUPATION_OTHER]: occupationOtherSchema,
  [CustomerSignUpField.PREFERRED_SUPPLIER_ID]: yup.number(),
  [ProfileField.FIRST_NAME]: requiredSchema,
  [ProfileField.LAST_NAME]: requiredSchema,
  [ProfileField.EMAIL]: requiredSchema,
});

export const signUpSchema2 = yup.object().shape({
  [AccountField.MOBILE]: mobileSchema,
  [AccountField.BUYING_BEHALF]: orderForSchema,
  // [AccountField.TERMS]: yup
  //   .boolean()
  //   .required('You must agree to the terms to place an order')
  //   .equals([true], 'You must agree to the terms to place an order'),
  [AccountField.VERIFICATION_CODE]: verificationSchema,
  [AccountField.REFERRAL]: yup.string(),
  [AccountField.REFERRAL_OTHER]: yup.string().when(AccountField.REFERRAL, {
    is: ReferralOption.OTHER,
    then: yup.string().required(TEXT_REQUIRED_MESSAGE),
  }),
  [CustomerSignUpField.COMPANY_NAME]: companyNameSchema,
  // [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: yup.string().required(TEXT_REQUIRED_MESSAGE),
  // [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: yup.string(),
  [CustomerSignUpField.OCCUPATION]: occupationSchema,
  [CustomerSignUpField.OCCUPATION_OTHER]: occupationOtherSchema,
  [CustomerSignUpField.PREFERRED_SUPPLIER_ID]: yup.number(),
  [ProfileField.FIRST_NAME]: requiredSchema,
  [ProfileField.LAST_NAME]: requiredSchema,
  [ProfileField.EMAIL]: requiredSchema,
});
